import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';

import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  CloudUpload,
  Delete,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { WellnubImageViewer } from '@wellnub/web-common';

import {
  useAddPartnerAggregator,
  useDeletePartnerPicture,
  usePartnerData,
  usePartnerRegister,
  useRemovePartnerAggregator,
  useUploadPartnerPicture,
} from 'hooks/partners.hook';
import {
  ACCEPTED_TYPES,
  MAX_UPLOAD_BYTES,
  compressImage,
} from 'utils/compress-image';

import { setModalStatus } from 'redux/slices/modal.slice';
import { setCurrentAggregator } from 'redux/slices/partner.slice';

import Button from '../../Button/Button.component';
import PictureCropper from '../../Cropper/PictureCropper.component';
import WorkdayScheduleTable from '../../Tables/WorkdayScheduleTable/WorkdayScheduleTable.component';
import DayOffScheduleTable from '../../Tables/DayOffScheduleTable/DayOffScheduleTable.component';

import { useStyles } from './PartnerForm.styles';

// Apaisada y fija: la foto se muestra después en el picker de locación, el
// dashboard del paciente y el ticket, y una proporción constante evita que esas
// vistas tengan que lidiar con fotos cuadradas o verticales.
const PICTURE_ASPECT = 16 / 9;

const PartnerForm = ({ editable, data }) => {
  const [hasError, setHasError] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState(false);
  const [passwordConfirmStatus, setPasswordConfirmStatus] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  // Object URL del archivo elegido, mientras el usuario lo recorta.
  const [pictureDraft, setPictureDraft] = useState(null);

  const { aggregators, data: partnerData } = useSelector(
    state => state.partner,
  );

  const dispatch = useDispatch();

  const { mutate: createPartner, isLoading: isCreateLoading } =
    usePartnerRegister();

  const { mutate: updatePartner, isLoading: isUpdateLoading } =
    usePartnerData();

  const { mutate: addPartnerAggregator } = useAddPartnerAggregator();

  const { mutate: removePartnerAggregator } = useRemovePartnerAggregator();

  const { mutate: uploadPicture, isLoading: isUploadingPicture } =
    useUploadPartnerPicture();

  const { mutate: deletePicture, isLoading: isDeletingPicture } =
    useDeletePartnerPicture();

  const { enqueueSnackbar } = useSnackbar();

  const isPictureBusy = isCompressing || isUploadingPicture || isDeletingPicture;

  const { control, handleSubmit, trigger } = useForm({
    defaultValues: editable ? data : {},
  });

  const classes = useStyles();

  const onAgregatorSwitchChangeHandler = aggregator => event => {
    batch(() => {
      dispatch(
        setModalStatus({
          name: 'ADD_PARTNER_AGGREGATOR_CONFIRMATION',
          isActive: true,
        }),
      );
      dispatch(setCurrentAggregator(aggregator));
    });
  };

  // La foto no pasa por el submit del formulario: tiene su propio endpoint, así
  // que el campo nunca bloquea el guardado del perfil.
  //
  // Elegir el archivo no sube nada todavía: abre el editor de recorte, y es al
  // confirmar ahí cuando se comprime y se envía.
  const onChangePictureHandler = event => {
    const input = event.target;
    const file = input.files?.[0];
    // Se limpia para que volver a elegir el mismo archivo dispare el onChange.
    input.value = '';

    if (!file) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      enqueueSnackbar('La fotografía debe ser JPG o PNG', {
        variant: 'warning',
      });
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      enqueueSnackbar('La fotografía no debe pesar más de 5 MB', {
        variant: 'warning',
      });
      return;
    }

    setPictureDraft(URL.createObjectURL(file));
  };

  const closeCropper = () => {
    setPictureDraft(null);
  };

  const onConfirmCropHandler = async croppedFile => {
    setIsCompressing(true);
    try {
      const optimized = await compressImage(croppedFile);
      const formData = new FormData();
      formData.append('file', optimized);
      // El editor se cierra hasta que la subida termina bien. Si falla, se
      // queda abierto con el recorte intacto para reintentar, en vez de
      // obligar a elegir el archivo otra vez.
      uploadPicture(
        { id: data._id, data: formData },
        { onSuccess: closeCropper },
      );
    } catch (error) {
      enqueueSnackbar('No se pudo procesar la fotografía, intenta con otra', {
        variant: 'error',
      });
    } finally {
      setIsCompressing(false);
    }
  };

  const onDeletePictureHandler = () => {
    deletePicture(data._id);
  };

  // Revoca el object URL cuando se reemplaza, al cerrar el editor y al
  // desmontar el formulario. Sin esto el blob se queda en memoria hasta que se
  // recargue la página.
  useEffect(() => {
    if (!pictureDraft) return undefined;
    return () => URL.revokeObjectURL(pictureDraft);
  }, [pictureDraft]);

  const onSubmitHandler = inputs => {
    const { inbodyScheduleTimes, closingDates, ...restInputs } = inputs;
    const { enabled, disabled } = aggregators.reduce(
      (accumulator, current) => {
        if (current.enabled) accumulator.enabled.push(current.name);
        else accumulator.disabled.push(current.name);
        return accumulator;
      },
      { enabled: [], disabled: [] },
    );
    if (editable) {
      if (!isEmpty(enabled)) {
        addPartnerAggregator({
          id: data.user._id,
          data: {
            permission: enabled[0],
          },
        });
      }
      if (!isEmpty(disabled)) {
        removePartnerAggregator({
          id: data.user._id,
          data: {
            permission: disabled[0],
          },
        });
      }
      updatePartner({
        id: data._id,
        data: { ...restInputs },
      });
    } else {
      createPartner(inputs);
    }
  };

  useEffect(() => {
    setHasError(!isEmpty(partnerData?.inbodyScheduleTimes));
  }, [partnerData]);

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Typography variant="h4" className={classes.title}>
        Datos generales para clientes externos
      </Typography>

      <Grid container spacing={4}>
        {/* --- Nombre --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="name"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Nombre"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={
                  error ? error.message : 'Escribe el nombre de tu gym/estudio'
                }
              />
            )}
          />
        </Grid>
        {/* --- Email --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="email"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="email"
                label="E-mail"
                value={value}
                error={!!error}
                disabled={editable}
                onChange={onChange}
                helperText={error ? error.message : 'user@example.com'}
              />
            )}
          />
        </Grid>
        {/* --- Tel. sucursal --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="phoneNumber"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Tel. sucursal"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : 'Teléfono fijo de contacto'}
              />
            )}
          />
        </Grid>

        {/* --- Tel. sucursal --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="website"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Sitio Web"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={
                  error ? error.message : 'Sitio web o página en redes sociales'
                }
              />
            )}
          />
        </Grid>

        {/* --- Tel. sucursal --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="address.street"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Dirección"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={
                  error
                    ? error.message
                    : 'Calle, número externo de casa, edificio, número de departamento / interior'
                }
              />
            )}
          />
        </Grid>

        {/* --- Tel. sucursal --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="address.neighborhood"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Colonia"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={
                  error ? error.message : 'Sitio web o página en redes sociales'
                }
              />
            )}
          />
        </Grid>

        {/* --- Ciudad --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="address.city"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Ciudad"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : 'Ciudad'}
              />
            )}
          />
        </Grid>

        {/* --- Estado --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="address.state"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Estado"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : 'Estado'}
              />
            )}
          />
        </Grid>

        {/* --- Código Postal --- */}
        <Grid item md={6} sm={12}>
          <Controller
            name="address.zipCode"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Código Postal"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : 'Código Postal'}
              />
            )}
          />
        </Grid>
        {/* --- Enlace Google Maps--- */}
        <Grid item md={6} sm={6} xs={12}>
          <Controller
            name="address.mapUrl"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Link Google Maps"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={
                  error
                    ? error.message
                    : 'Introduce el enlace de tu ubicación en Google Maps'
                }
              />
            )}
          />
        </Grid>
        {/* --- Indicaciones --- */}
        <Grid item md={12} sm={12}>
          <Controller
            name="indications"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                multiline
                rows={4}
                type="text"
                label="Otras indicaciones"
                value={value}
                error={!!error}
                variant="outlined"
                onChange={onChange}
                helperText={error ? error.message : ''}
              />
            )}
          />
        </Grid>
        {/* --- Fotografia del establecimiento --- */}
        {editable && (
          <Grid item md={12} sm={12}>
            <Typography variant="subtitle1">
              Fotografía del establecimiento
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.paragraph}
            >
              Opcional. Ayuda a que el paciente localice el establecimiento. JPG
              o PNG de hasta 5 MB; se optimiza automáticamente antes de
              guardarse.
            </Typography>
            {data?.pictureUrl ? (
              <Box display="flex" alignItems="flex-start">
                <Box className={classes.picturePreview}>
                  <WellnubImageViewer url={data.pictureUrl} />
                </Box>
                <IconButton
                  aria-label="Eliminar fotografía"
                  onClick={onDeletePictureHandler}
                  disabled={isPictureBusy}
                >
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sin fotografía.
              </Typography>
            )}
            <Box mt={2}>
              {/* El `disabled` va también en el input: un <label> activa su
                  control asociado aunque el botón que envuelve esté
                  deshabilitado, así que sin esto se podría elegir otro archivo
                  mientras la subida anterior está en curso. */}
              <input
                accept={ACCEPTED_TYPES.join(',')}
                type="file"
                style={{ display: 'none' }}
                id="partnerPictureInput"
                disabled={isPictureBusy}
                onChange={onChangePictureHandler}
              />
              <label htmlFor="partnerPictureInput">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  loading={isPictureBusy}
                >
                  {data?.pictureUrl
                    ? 'Reemplazar fotografía'
                    : 'Subir fotografía'}
                </Button>
              </label>
            </Box>
            {pictureDraft && (
              <PictureCropper
                src={pictureDraft}
                aspect={PICTURE_ASPECT}
                isLoading={isPictureBusy}
                onCancel={closeCropper}
                onConfirm={onConfirmCropHandler}
              />
            )}
          </Grid>
        )}
      </Grid>
      <br />
      <br />
      <Typography variant="h4" className={classes.title}>
        Inbody asignado
      </Typography>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <Controller
            name="inbodyNumber"
            defaultValue=""
            control={control}
            rules={{
              required: 'Este campo es requerido',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                type="text"
                label="Número de serie"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : 'xxx-xxx-xxx'}
              />
            )}
          />
        </Grid>
      </Grid>

      {editable && (
        <>
          <br />
          <br />
          <Typography variant="h4" className={classes.title}>
            Agregador
          </Typography>
          <Grid container spacing={1}>
            {aggregators.map(aggregator => (
              <Grid item xs key={aggregator.name}>
                <FormControlLabel
                  label={
                    {
                      checkInExternalCompany: 'FitPass',
                    }[aggregator.name]
                  }
                  control={
                    <Switch
                      checked={aggregator.enabled}
                      name={aggregator.name}
                      onChange={onAgregatorSwitchChangeHandler(aggregator)}
                      color="primary"
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!editable && (
        <>
          <Divider className={classes.divider} />
          <Typography variant="h4" className={classes.title}>
            Contraseña
          </Typography>
          <Grid container spacing={4}>
            <Grid item md={6} sm={12}>
              <Controller
                name="password"
                defaultValue=""
                control={control}
                rules={{
                  required: 'Este campo es requerido',
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    type={passwordStatus ? 'text' : 'password'}
                    label="Contraseña"
                    value={value}
                    error={!!error}
                    onChange={onChange}
                    helperText={error ? error.message : 'Escribe tu contraseña'}
                    InputProps={{
                      autoComplete: 'new-password',
                      endAdornment: (
                        <IconButton
                          onClick={() =>
                            setPasswordStatus(prevState => !prevState)
                          }
                        >
                          {passwordStatus ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} sm={12}>
              <Controller
                name="confirmPassword"
                defaultValue=""
                control={control}
                rules={{
                  required: 'Este campo es requerido',
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    type={passwordConfirmStatus ? 'text' : 'password'}
                    label="Confirma contraseña"
                    value={value}
                    error={!!error}
                    onChange={onChange}
                    helperText={
                      error ? error.message : 'Confirma tu contraseña'
                    }
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() =>
                            setPasswordConfirmStatus(prevState => !prevState)
                          }
                        >
                          {passwordConfirmStatus ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Box pt={4} textAlign="right">
        <Button
          type="submit"
          color="primary"
          variant="contained"
          loading={isUpdateLoading || isCreateLoading}
        >
          {editable ? 'Actualizar' : 'Agregar'}
        </Button>
      </Box>
      {editable && (
        <>
          <Divider className={classes.divider} />
          <Typography variant="h4" className={classes.title}>
            Horario de atención para pruebas Inbody
          </Typography>
          <Typography className={classes.paragraph}>
            Por favor indica los días y horarios en que los clientes externos
            pueden acudir a realizarse escaneos corporales.
          </Typography>
          <WorkdayScheduleTable />
          <br />
          <Controller
            defaultValue=""
            control={control}
            rules={{
              validate: () => {
                return (
                  hasError || 'Debes de agregar al menos un horario de atención'
                );
              },
            }}
            name="hiddenWorkDays"
            render={({ fieldState: { error } }) => (
              <TextField
                fullWidth
                type="hidden"
                error={!!error}
                helperText={error ? error.message : ' '}
              />
            )}
          />
          <br />
          <br />
          <br />
          <Typography variant="h4" className={classes.title}>
            Días sin servicio
          </Typography>
          <Typography className={classes.paragraph}>
            Por favor indica los días y horarios del año en los que no será
            posible que los clientes acudan a realizarse escaneos corporales.
          </Typography>
          <DayOffScheduleTable />
        </>
      )}
    </form>
  );
};

export default PartnerForm;
